import { Box, Card, CardContent, Tab, Tabs, useMediaQuery } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { Batch } from './batch';
import SalaryAccounts from './salaryAccounts';
import { PayroleSettings } from './settings';
import { Expenses } from './expenses';
import { DirectPayrollExtras } from './directExtras';
import { Contracts } from './contracts';
import MDBox from 'components/MDBox';
import { CurrencyRupee, Description, ListAltOutlined, Paid, Receipt, Settings } from '@mui/icons-material';
import colors from 'assets/theme/base/colors';
import MDTypography from 'components/MDTypography';
import rgba from 'assets/theme/functions/rgba';


function PayroleTabs(props) {
	const [activeTab, setActiveTab] = useState({
		value: 0,
		label: 'Account & Salary'
	}); // State for active tab
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const navigate = useNavigate();
	const { tab } = useParams();
	const tabsConfig = [
	{
		label: "Account & Salary",
		key: "accounts",
		icon: <CurrencyRupee />,
	},
	{
		label: "Salary Slip",
		key: "salary-slips",
		icon: <Receipt />,
	},

	...(props?.permissions?.includes(
		props?.modulePermissions?.Batch?.list_permission
	)
		? [
				{
					label: "Salary Batches",
					key: "batches",
					icon: <ListAltOutlined />,
				},
		  ]
		: []),

	...(props?.permissions?.includes(
			props?.modulePermissions?.Expenses?.list_permission
		) ||
		props?.permissions?.includes(
			props?.modulePermissions?.Expenses?.owner_list_permission
		)
		? [
				{
					label: "Expenses",
					key: "expenses",
					icon: <Paid />,
				},
		  ]
		: []),

	...(props?.permissions?.includes(
			props?.modulePermissions?.Expenses?.update_permission
		) ||
		props?.permissions?.includes(
			props?.modulePermissions?.Expenses?.approve_permission
		)
		? [
				{
					label: "HR Extras",
					key: "hr-extras",
					icon: <Paid />,
				},
		  ]
		: []),

	...(props?.permissions?.includes(
		props?.modulePermissions?.Contracts?.list_permission
	)
		? [
				{
					label: "Contracts",
					key: "contracts",
					icon: <Description />,
				},
		  ]
		: []),

	...(props?.permissions?.includes(
		props?.modulePermissions?.payroleSettings?.salary_head_list_permission
	)
		? [
				{
					label: "Settings",
					key: "settings",
					icon: <Settings />,
				},
		  ]
		: []),
];
	useEffect(() => {
	if (!tab) {
		navigate(`/payroll/${tabsConfig[0].key}`, {
			replace: true,
		});
		return;
	}

	const tabIndex = tabsConfig.findIndex(
		(item) => item.key === tab
	);

	if (tabIndex >= 0) {
		setActiveTab(tabIndex);
	} else {
		navigate(`/payroll/${tabsConfig[0].key}`, {
			replace: true,
		});
	}
}, [tab, navigate]);

	const handleTabChange = (_, newValue) => {
	setActiveTab(newValue);

	navigate(`/payroll/${tabsConfig[newValue].key}`);
};

	const renderTabContent = () => {
	const currentTab = tabsConfig[activeTab]?.key;

	switch (currentTab) {
		case "accounts":
			return (
				<SalaryAccounts
					action="AccountsSalary"
					modulePermissions={props.modulePermissions.Payrole}
					permissions={props.permissions}
				/>
			);

		case "salary-slips":
			return (
				<SalaryAccounts
					action="SalarySlips"
					modulePermissions={props.modulePermissions.Payrole}
					permissions={props.permissions}
				/>
			);

		case "batches":
			return (
				<Batch
					modulePermissions={props.modulePermissions.Payrole}
					permissions={props.permissions}
				/>
			);

		case "expenses":
			return (
				<Expenses
					modulePermissions={props.modulePermissions.Expenses}
					permissions={props.permissions}
				/>
			);

		case "hr-extras":
			return (
				<DirectPayrollExtras
					modulePermissions={props.modulePermissions.Expenses}
					permissions={props.permissions}
				/>
			);

		case "contracts":
			return (
				<Contracts
					modulePermissions={props.modulePermissions.Payrole}
					permissions={props.permissions}
				/>
			);

		case "settings":
			return (
				<PayroleSettings
					permissions={props.permissions}
					modulePermissions={
						props.modulePermissions.payroleSettings
					}
				/>
			);

		default:
			return null;
	}
};

	const tabStyles = {
		minHeight: 46,
		borderRadius: "10px",
		px: 2.1,
		py: 0.8,
		textTransform: "none",
		color: colors.text.main,
		fontWeight: 700,
		transition: "all 180ms ease",
		"& .MuiTab-iconWrapper": {
			mb: "0 !important",
		},
		"&:hover": {
			backgroundColor: rgba(colors.background.muted, 0.42),
			color: colors.dark.main,
		},
		"&.Mui-selected": {
			color: colors.dark.main,
			background: `linear-gradient(335deg, ${colors.light.main} 0%, ${colors.info.main} 150%)`,
			boxShadow: `0 10px 22px ${rgba(colors.dark.main, 0.08)}`,
		},
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox sx={{ p: 3 }}>
				<MDBox style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<MDBox
						sx={{
							width: 48,
							height: 48,
							borderRadius: "16px",
							display: "grid",
							placeItems: "center",
							background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
							color: colors.white.main,
						}}
					>
						<CurrencyRupee />
					</MDBox>
					<MDBox>
						<MDTypography variant="h5">Payroll Management</MDTypography>
						<MDTypography variant="body2" color="text">
							Manage employee compensation, payroll, and financial records in one place.
						</MDTypography>
					</MDBox>
				</MDBox>
				<Box sx={{ marginTop: '24px', padding: '6px', borderRadius: "15px", border: 'none', display: "flex", flexDirection: isSmallScreen ? "column" : "row", alignItems: "center", justifyContent: isSmallScreen ? "flex-start" : "center", gap: 1, marginBottom: '14px', }}>
					<Tabs
						value={activeTab}
						onChange={handleTabChange}
						centered={!isSmallScreen}
						orientation={isSmallScreen ? "vertical" : "horizontal"}
						sx={{
							width: "100%",
							"& .MuiTab-root": {
								fontSize: isSmallScreen ? "0.8rem" : "1rem",
							},
						}}
					>
						{tabsConfig.map((tab) => (
							<Tab
								key={tab.key}
								label={tab.label}
								icon={tab.icon}
								sx={tabStyles}
							/>
						))}
</Tabs>
				</Box>
				{/* Render tab content dynamically */}
				<Box sx={{ marginTop: 2 }}>
					{renderTabContent()}
				</Box>
			</MDBox>
		</DashboardLayout>
	);
}
export default PayroleTabs;
